import React, { useState, useEffect, useCallback } from "react";
import { motion } from "motion/react";
import {
  Target, MessageSquare, CheckCircle, Save,
  Loader2, AlertTriangle, Eye, User
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useAutoSave } from "../../hooks/useAutoSave";
import AutoSaveIndicator from "./AutoSaveIndicator";

export default function ExpertGoalSetting({ booking, onSaved, onGoalSet }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clientBrief, setClientBrief] = useState(null);

  const [form, setForm] = useState({
    session_goal: "",
    session_deliverable: "",
    expert_questions: ""
  });

  const deliverableOptions = [
    "Клиент уйдет с конкретным планом действий на 2-4 недели",
    "Клиент получит экспертную оценку и разбор ситуации",
    "Клиент поймет корневые причины проблемы",
    "Клиент получит фреймворк для принятия решений",
    "Другое"
  ];

  const buildPayload = useCallback((formData) => ({
    session_goal: formData.session_goal,
    session_deliverable: formData.session_deliverable,
    expert_questions: formData.expert_questions
  }), []);

  const buildInsertPayload = useCallback((formData) => ({
    session_goal: formData.session_goal,
    session_deliverable: formData.session_deliverable,
    expert_questions: formData.expert_questions,
    booking_id: booking.id,
    expert_id: booking.expertId,
    client_id: booking.clientId
  }), [booking.id, booking.expertId, booking.clientId]);

  const {
    autoSaving,
    lastAutoSaved,
    restoredFromDraft,
    setRestoredFromDraft,
    scheduleAutoSave,
    cancelAutoSave,
    markSaved,
    loadFromLocal,
    clearLocal
  } = useAutoSave({
    table: "expert_session_goals",
    bookingId: booking.id,
    form,
    buildPayload,
    buildInsertPayload,
    enabled: !loading
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [briefResult, goalsResult] = await Promise.all([
          supabase
            .from("client_session_briefs")
            .select("*")
            .eq("booking_id", booking.id)
            .maybeSingle(),
          supabase
            .from("expert_session_goals")
            .select("*")
            .eq("booking_id", booking.id)
            .maybeSingle()
        ]);

        if (briefResult.data) setClientBrief(briefResult.data);

        if (goalsResult.data) {
          setForm({
            session_goal: goalsResult.data.session_goal || "",
            session_deliverable: goalsResult.data.session_deliverable || "",
            expert_questions: goalsResult.data.expert_questions || ""
          });
        } else {
          const localDraft = loadFromLocal();
          if (localDraft) {
            setForm(localDraft);
            setRestoredFromDraft(true);
          }
        }
      } catch (err) {
        console.warn("Failed to load prep data:", err);
        const localDraft = loadFromLocal();
        if (localDraft) {
          setForm(localDraft);
          setRestoredFromDraft(true);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [booking.id, loadFromLocal, setRestoredFromDraft]);

  const handleSave = async () => {
    cancelAutoSave();
    setSaving(true);
    try {
      const { data: existing } = await supabase
        .from("expert_session_goals")
        .select("id")
        .eq("booking_id", booking.id)
        .maybeSingle();

      const payload = {
        session_goal: form.session_goal,
        session_deliverable: form.session_deliverable,
        expert_questions: form.expert_questions,
        updated_at: new Date().toISOString()
      };

      if (existing) {
        await supabase
          .from("expert_session_goals")
          .update(payload)
          .eq("id", existing.id);
      } else {
        await supabase
          .from("expert_session_goals")
          .insert({
            ...payload,
            booking_id: booking.id,
            expert_id: booking.expertId,
            client_id: booking.clientId
          });
      }

      markSaved();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (onSaved) onSaved(form);
      if (onGoalSet) onGoalSet({ ...form, clientBrief });
    } catch (err) {
      console.error("Save expert goals error:", err);
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => {
    const newForm = { ...form, [field]: value };
    setForm(newForm);
    scheduleAutoSave(newForm);
  };

  if (loading) {
    return (
      <div id="expert-goal-setting_loading" className="p-6 text-center">
        <Loader2 className="w-6 h-6 text-primary-400 animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <motion.div
      id="expert-goal-setting_root"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {clientBrief && (clientBrief.task_description || clientBrief.focus_area) && (
        <div className="p-5 rounded-md bg-accent-950/20 border border-accent-600/20">
          <div className="flex items-center gap-2 mb-3">
            <User className="w-5 h-5 text-accent-400" />
            <h4 className="text-lg font-medium text-accent-300">
              Запрос клиента: {booking.clientName}
            </h4>
          </div>
          <div className="space-y-2">
            {clientBrief.focus_area && (
              <div className="flex items-start gap-2">
                <span className="text-lg font-extralight text-neutral-500 flex-shrink-0">Направление:</span>
                <span className="text-lg font-extralight text-neutral-200">{clientBrief.focus_area}</span>
              </div>
            )}
            {clientBrief.task_description && (
              <div className="flex items-start gap-2">
                <span className="text-lg font-extralight text-neutral-500 flex-shrink-0">Задача:</span>
                <span className="text-lg font-extralight text-neutral-200">{clientBrief.task_description}</span>
              </div>
            )}
            {clientBrief.current_situation && (
              <div className="flex items-start gap-2">
                <span className="text-lg font-extralight text-neutral-500 flex-shrink-0">Контекст:</span>
                <span className="text-lg font-extralight text-neutral-200">{clientBrief.current_situation}</span>
              </div>
            )}
            {clientBrief.expected_outcome && (
              <div className="flex items-start gap-2">
                <span className="text-lg font-extralight text-neutral-500 flex-shrink-0">Ожидание:</span>
                <span className="text-lg font-extralight text-neutral-200">{clientBrief.expected_outcome}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {!clientBrief?.task_description && (
        <div className="p-4 rounded-md bg-neutral-800/30 border border-neutral-700/50">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-neutral-500" />
            <p className="text-lg font-extralight text-neutral-500">
              Клиент ещё не заполнил опросник перед встречей
            </p>
          </div>
        </div>
      )}

      <div className="p-5 rounded-md bg-neutral-900 border border-neutral-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary-400" />
            <h4 className="text-lg font-medium text-white">Ваше целеполагание</h4>
          </div>
          <AutoSaveIndicator
            autoSaving={autoSaving}
            lastAutoSaved={lastAutoSaved}
            restoredFromDraft={restoredFromDraft}
            onDismissRestore={() => setRestoredFromDraft(false)}
          />
        </div>

        <div className="space-y-4">
          <div>
            <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
              <Target className="w-4 h-4 text-primary-400" />
              Какую главную цель вы видите для этой сессии?
            </label>
            <textarea
              value={form.session_goal}
              onChange={(e) => update("session_goal", e.target.value)}
              rows={5}
              placeholder="Помочь клиенту определить приоритетные каналы привлечения и выстроить план тестирования..."
              className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-primary-500 resize-none transition-colors"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              Чем хотите закончить встречу? (клиент уйдет с...)
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {deliverableOptions.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => update("session_deliverable", option)}
                  className={`px-3 py-1.5 rounded-md text-lg font-extralight transition-all ${
                    form.session_deliverable === option
                      ? "bg-green-600/20 text-green-300 border border-green-500/40"
                      : "bg-neutral-800/50 text-neutral-400 border border-neutral-700/50 hover:border-neutral-600"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 text-lg font-extralight text-neutral-300 mb-2">
              <MessageSquare className="w-4 h-4 text-blue-400" />
              Какие вопросы хотите задать клиенту? (опционально)
            </label>
            <textarea
              value={form.expert_questions}
              onChange={(e) => update("expert_questions", e.target.value)}
              rows={5}
              placeholder="Вопросы, которые помогут лучше понять ситуацию клиента..."
              className="w-full px-4 py-3 rounded-md bg-neutral-800 border border-neutral-700 text-white text-lg font-extralight placeholder:text-neutral-600 focus:outline-none focus:border-blue-500 resize-none transition-colors"
            />
          </div>
        </div>

        <div className="flex items-center justify-between mt-4">
          <div>
            {saved && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-2 text-accent-400"
              >
                <CheckCircle className="w-5 h-5" />
                <span className="text-lg font-extralight">Сохранено</span>
              </motion.div>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-600 hover:bg-primary-500 disabled:bg-neutral-700 text-white text-lg font-medium transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Сохранение..." : "Сохранить целеполагание"}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
